<!-- eslint-disable vue/no-bare-strings-in-template -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import { formatCurrency } from '../utils/crmPageUtils';
import {
  houseAddressLine,
  houseDetailsLine,
} from '../utils/visitorInfoPresenter';

const props = defineProps({
  title: { type: String, required: true },
  emptyText: { type: String, required: true },
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  icon: { type: String, default: 'i-lucide-home' },
  metaPrefix: { type: String, default: '' },
  entryLabel: { type: String, default: 'Entrada' },
  actionLabel: { type: String, default: 'Abrir imóvel' },
});

const emit = defineEmits(['entry-info', 'view-listing']);

const houseFromItem = item => item?.house || item?.imovel || item || {};

const houseId = item => {
  const house = houseFromItem(item);
  return house?.house_id || house?.id || item?.imovel_id || item?.house_id;
};

const itemId = item =>
  item?.favorite_id || item?.visita_id || item?.visit_code || houseId(item);

const thumbnailSrc = item => {
  const thumbnail = houseFromItem(item)?.thumbnail;
  if (!thumbnail) return '';
  if (typeof thumbnail === 'string') return thumbnail;
  return thumbnail.url || thumbnail.src || thumbnail.path || '';
};

const priceLabel = item => {
  const house = houseFromItem(item);
  return formatCurrency(
    house?.rent_price ||
      house?.rent ||
      house?.valor_aluguel ||
      house?.sale_price
  );
};

const dateLabel = item => {
  const value =
    item?.favorited_at ||
    item?.favoritedAt ||
    item?.visited_at ||
    item?.visitedAt;
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};

const metaLabel = item =>
  [
    item?.list_name || item?.status,
    dateLabel(item) && `${props.metaPrefix} ${dateLabel(item)}`,
  ]
    .filter(Boolean)
    .join(' · ');
</script>

<template>
  <LaisCard :title="title" :prefix-icon="icon" density="tight">
    <div v-if="loading" class="grid gap-3 sm:grid-cols-2">
      <span class="h-28 w-full animate-pulse rounded bg-lais-bg-muted" />
      <span class="h-28 w-full animate-pulse rounded bg-lais-bg-muted" />
    </div>

    <div v-else-if="items.length" class="grid gap-3 sm:grid-cols-2">
      <article
        v-for="item in items"
        :key="itemId(item)"
        class="overflow-hidden rounded-lais-sm border border-lais-border-soft bg-white"
      >
        <div class="flex min-h-[112px] gap-3 p-3">
          <img
            v-if="thumbnailSrc(item)"
            :src="thumbnailSrc(item)"
            :alt="
              houseFromItem(item).title || houseAddressLine(houseFromItem(item))
            "
            class="h-20 w-28 shrink-0 rounded-lais-sm object-cover"
          />
          <div
            v-else
            class="grid h-20 w-28 shrink-0 place-items-center rounded-lais-sm bg-lais-bg-muted text-lais-text-muted"
          >
            <span class="i-lucide-image size-5" aria-hidden="true" />
          </div>
          <div class="min-w-0 flex-1">
            <p class="font-semibold text-lais-text">
              {{ priceLabel(item) || houseFromItem(item).title || 'Imóvel' }}
            </p>
            <p class="mt-1 line-clamp-2 text-lais-body-sm text-lais-text-muted">
              {{ houseAddressLine(houseFromItem(item)) }}
            </p>
            <p class="mt-1 truncate text-lais-body-sm text-lais-text-muted">
              {{ houseDetailsLine(houseFromItem(item)) }}
            </p>
            <p
              v-if="metaLabel(item)"
              class="mt-1 truncate text-[12px] font-semibold text-lais-text-muted"
            >
              {{ metaLabel(item) }}
            </p>
          </div>
        </div>
        <div
          class="flex items-center justify-between gap-2 border-t border-lais-border-soft px-3 py-2"
        >
          <button
            type="button"
            class="text-lais-label-sm font-semibold text-lais-primary hover:text-lais-primary-600"
            @click="emit('entry-info', houseFromItem(item))"
          >
            {{ entryLabel }}
          </button>
          <button
            type="button"
            class="inline-flex h-8 items-center gap-2 rounded-full px-2 text-lais-label-sm font-semibold text-lais-text-muted hover:bg-lais-primary-50 hover:text-lais-primary"
            :aria-label="actionLabel"
            :title="actionLabel"
            @click="emit('view-listing', houseFromItem(item))"
          >
            <span class="i-lucide-external-link size-4" aria-hidden="true" />
            {{ actionLabel }}
          </button>
        </div>
      </article>
    </div>

    <div
      v-else
      class="rounded-lais-sm border border-dashed border-lais-border-soft px-3 py-4 text-[13px] text-lais-text-muted"
    >
      {{ emptyText }}
    </div>
  </LaisCard>
</template>
